import { normalise } from "./steps/Validation/engine/valueParsing";

// One uploaded sheet feeding a template worksheet
export type SheetSource = {
  sheetName: string;
  worksheet: string;
  // Lowest order first; the main sheet's rows become the worksheet's rows
  sourceOrder: number;
  keyColumnName: string | null;
};

// One column of a worksheet and the sheet it is read from
export type ColumnSource = {
  // Unique within the worksheet; what mappings and the output file use
  columnName: string;
  sheetName: string;
  sourceColumn: string;
  sourceOrder: number;
};

export type SheetTarget = {
  worksheet: string;
  // Identifies the sheet among the worksheet's sources; the lowest is the main sheet.
  // Only changed by choosing another main sheet, which swaps two sheets' orders.
  sourceOrder: number;
  keyColumnName: string | null;
  matchedBySheetName: boolean;
};

// Excel mode only: one workbook sheet and the template worksheets it feeds
export type WorkbookSheetEntry = {
  sheetName: string;
  // Empty = "Not provided" — unassociated sheets are ignored downstream.
  targets: SheetTarget[];
};

// One sheet linked to one of its target worksheets
export type SheetLink = { sheetName: string; target: SheetTarget };

export type SheetSourceDescription = {
  isMainSheet: boolean;
  hint: string;
  missingKeyColumnMessage: string;
};

// Goes between a sheet name and one of its column names, e.g. "Tenancy Listing → Tenancy Code"
export const SHEET_COLUMN_SEPARATOR = " → ";

export function isMultiSourceWorksheet(columnSources: ColumnSource[]): boolean {
  return new Set(columnSources.map((source) => source.sheetName)).size > 1;
}

// Names every column of a worksheet built from several sheets. A later sheet's column whose name is
// already taken is prefixed with its sheet name, e.g. "Tenancy Listing → Tenancy Code".
export function buildColumnSources(sources: Array<SheetSource & { columns: string[] }>): ColumnSource[] {
  const takenNames = new Set<string>();
  return sortBySourceOrder(sources).flatMap(({ sheetName, sourceOrder, columns }, sourceIndex) =>
    columns.map((sourceColumn) => {
      const isTaken = sourceIndex > 0 && takenNames.has(normalise(sourceColumn));
      const columnName = isTaken ? `${sheetName}${SHEET_COLUMN_SEPARATOR}${sourceColumn}` : sourceColumn;
      takenNames.add(normalise(columnName));
      return { columnName, sheetName, sourceColumn, sourceOrder };
    })
  );
}

// Each worksheet's columns, named the way buildColumnSources names them
export function buildWorksheetColumnSources(
  sources: Array<SheetSource & { columns: string[] }>
): Record<string, ColumnSource[]> {
  const worksheets = [...new Set(sources.map((source) => source.worksheet))];
  return Object.fromEntries(
    worksheets.map((worksheet) => [
      worksheet,
      buildColumnSources(sources.filter((source) => source.worksheet === worksheet)),
    ])
  );
}

// Names a column by its sheet's source order and its header there, so the name doesn't change with another sheet's headers
export function buildColumnKey(sourceOrder: number, sourceColumn: string): string {
  return JSON.stringify([sourceOrder, sourceColumn]);
}

// Undefined when the value isn't a column key
export function parseColumnKey(columnKey: string): { sourceOrder: number; sourceColumn: string } | undefined {
  try {
    const parsed: unknown = JSON.parse(columnKey);
    if (Array.isArray(parsed) && typeof parsed[0] === "number" && typeof parsed[1] === "string") {
      return { sourceOrder: parsed[0], sourceColumn: parsed[1] };
    }
    return undefined;
  } catch {
    return undefined;
  }
}

// The column a key points at; undefined when its sheet no longer has that header
export function findColumnSource(columnSources: ColumnSource[], columnKey: string): ColumnSource | undefined {
  const column = parseColumnKey(columnKey);
  if (!column) return undefined;
  const header = normalise(column.sourceColumn);
  return columnSources.find(
    (source) => source.sourceOrder === column.sourceOrder && normalise(source.sourceColumn) === header
  );
}

// The key's header, after its sheet name when the worksheet has several sheets, e.g. "Tenancy Listing → Client Name".
// A value that isn't a column key is shown as it is.
export function describeColumnKey(columnSources: ColumnSource[], columnKey: string): string {
  const column = parseColumnKey(columnKey);
  if (!column) return columnKey;
  const sheetName = columnSources.find((source) => source.sourceOrder === column.sourceOrder)?.sheetName;
  return sheetName && isMultiSourceWorksheet(columnSources)
    ? `${sheetName}${SHEET_COLUMN_SEPARATOR}${column.sourceColumn}`
    : column.sourceColumn;
}

export function sortBySourceOrder<T extends { sourceOrder: number }>(sources: T[]): T[] {
  return [...sources].sort((a, b) => a.sourceOrder - b.sourceOrder);
}

// Each sheet-to-worksheet link; "Not provided" sheets are left out
export function listSheetSources(sheets: WorkbookSheetEntry[]): SheetSource[] {
  return listSheetLinks(sheets).map(({ sheetName, target }) => ({
    sheetName,
    worksheet: target.worksheet,
    sourceOrder: target.sourceOrder,
    keyColumnName: target.keyColumnName,
  }));
}

// Each worksheet's linked sheets, lowest source order first
export function groupSheetLinksByWorksheet(sheets: WorkbookSheetEntry[]): Map<string, SheetLink[]> {
  const linksByWorksheet = new Map<string, SheetLink[]>();
  for (const link of listSheetLinks(sheets)) {
    linksByWorksheet.set(link.target.worksheet, [...(linksByWorksheet.get(link.target.worksheet) ?? []), link]);
  }
  for (const links of linksByWorksheet.values()) {
    links.sort((a, b) => a.target.sourceOrder - b.target.sourceOrder);
  }
  return linksByWorksheet;
}

// Sheets linked to the worksheet, lowest source order first
export function listLinkedSheets(sheets: WorkbookSheetEntry[], worksheet: string): SheetLink[] {
  return groupSheetLinksByWorksheet(sheets).get(worksheet) ?? [];
}

export function hasMultiSourceWorksheet(sheets: WorkbookSheetEntry[]): boolean {
  return listMultiSourceLinks(sheets).length > 0;
}

// True when a worksheet fed by more than one sheet has a sheet with no key column chosen
export function hasMissingKeyColumn(sheets: WorkbookSheetEntry[]): boolean {
  return listMultiSourceLinks(sheets).some((link) => !link.target.keyColumnName?.trim());
}

// What the Upload step shows under a sheet's link to a shared worksheet. Null when the sheet is the only source.
export function describeSheetSource(
  sheets: WorkbookSheetEntry[],
  sheetName: string,
  worksheet: string
): SheetSourceDescription | null {
  const sources = listLinkedSheets(sheets, worksheet);
  const position = sources.findIndex((link) => link.sheetName === sheetName) + 1;
  if (sources.length < 2 || position === 0) return null;

  const mainSheet = sources[0];
  if (position === 1) {
    return {
      isMainSheet: true,
      hint: `Rows for ${worksheet} come from this sheet. Other sheets only add columns.`,
      missingKeyColumnMessage: "Choose the column the other sheets are matched on.",
    };
  }

  const mainSheetKeyColumn = mainSheet.target.keyColumnName?.trim() || "the key column";
  return {
    isMainSheet: false,
    hint: `Source ${position} of ${sources.length} · matched to ${mainSheet.sheetName} on the key column`,
    missingKeyColumnMessage: `Choose the column that matches ${mainSheetKeyColumn} on ${mainSheet.sheetName}.`,
  };
}

// Makes the sheet the worksheet's main sheet by swapping source orders with the current main sheet.
// The sheets come back unchanged when the sheet is already the main sheet or isn't linked to the worksheet.
export function chooseMainSheet(
  sheets: WorkbookSheetEntry[],
  worksheet: string,
  sheetName: string
): WorkbookSheetEntry[] {
  const sources = listLinkedSheets(sheets, worksheet);
  const mainSheet = sources[0];
  const chosenSheet = sources.find((link) => link.sheetName === sheetName);
  if (!mainSheet || !chosenSheet || chosenSheet === mainSheet) return sheets;

  const swappedSourceOrders = new Map([
    [mainSheet.sheetName, chosenSheet.target.sourceOrder],
    [chosenSheet.sheetName, mainSheet.target.sourceOrder],
  ]);
  return sheets.map((sheet) => {
    const sourceOrder = swappedSourceOrders.get(sheet.sheetName);
    if (sourceOrder === undefined) return sheet;
    return {
      ...sheet,
      targets: sheet.targets.map((target) => (target.worksheet === worksheet ? { ...target, sourceOrder } : target)),
    };
  });
}

// Worksheets whose main sheet is a different sheet after the change. A worksheet left with no sheets isn't listed.
export function listWorksheetsWithNewMainSheet(
  currentSheets: WorkbookSheetEntry[],
  nextSheets: WorkbookSheetEntry[]
): string[] {
  const nextLinksByWorksheet = groupSheetLinksByWorksheet(nextSheets);
  return [...groupSheetLinksByWorksheet(currentSheets)]
    .filter(([worksheet, [currentMainSheet]]) => {
      const nextMainSheet = nextLinksByWorksheet.get(worksheet)?.[0];
      return nextMainSheet !== undefined && nextMainSheet.sheetName !== currentMainSheet.sheetName;
    })
    .map(([worksheet]) => worksheet);
}

function listSheetLinks(sheets: WorkbookSheetEntry[]): SheetLink[] {
  return sheets.flatMap(({ sheetName, targets }) => targets.map((target) => ({ sheetName, target })));
}

function listMultiSourceLinks(sheets: WorkbookSheetEntry[]): SheetLink[] {
  return [...groupSheetLinksByWorksheet(sheets).values()].filter((links) => links.length > 1).flat();
}
